import { getMessaging, getToken, Messaging, onMessage } from 'firebase/messaging'
import { getAnalytics } from 'firebase/analytics'
import { getPerformance } from 'firebase/performance'
import { getApps, getApp, initializeApp } from 'firebase/app'
import {
  inMemoryPersistence,
  getAuth,
  onAuthStateChanged,
  onIdTokenChanged,
  setPersistence,
  signOut,
  User,
  browserLocalPersistence
} from 'firebase/auth'

import env from '@app/environment'

const BRAND_ICON = `${window.location.origin}/assets/branding/logotype-entry-x.png`

export const firebaseConfig = {
  apiKey: env.FIREBASE_API_KEY,
  authDomain: env.FIREBASE_AUTH_DOMAIN,
  projectId: env.FIREBASE_PROJECT_ID,
  storageBucket: env.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.FIREBASE_MESSAGING_SENDER_ID,
  appId: env.FIREBASE_APP_ID,
  measurementId: env.FIREBASE_MEASUREMENT_ID,
}

const app = getApps().length ? getApp() : firebaseConfig && initializeApp(firebaseConfig)
const auth = app? getAuth(app): null

let persistencePromise: Promise<void> | null = null
const STORAGE_KEY = '__firebase_persistence_set__'
let memoryCache: 'local' | 'memory' | null = null

let userPromise: Promise<User | null> | null = null
let userResolver: ((u: User | null) => void) | null = null

let tokenPromise: Promise<string | null> | null = null
let tokenResolver: ((token: string | null) => void) | null = null

function safeLocalStorageAvailable() {
  try {
    const testKey = '__firebase_test__'
    localStorage.setItem(testKey, '1')
    localStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

function readPersistenceFlag(): 'local' | 'memory' | null {
  if (memoryCache) return memoryCache
  if (!safeLocalStorageAvailable()) return null

  const val = localStorage.getItem(STORAGE_KEY)
  if (val === 'local' || val === 'memory') {
    memoryCache = val
    return val
  }
  return null
}

function writePersistenceFlag(value: 'local' | 'memory') {
  memoryCache = value
  if (safeLocalStorageAvailable()) {
    try {
      localStorage.setItem(STORAGE_KEY, value)
    } catch(e) {
      console.warn(e)
    }
  }
}


function waitForAuthInit(auth = getAuth()) {
  return new Promise<void>((resolve) => {
    const unsub = onAuthStateChanged(auth, () => {
      unsub()
      resolve()
    })
  })
}


export function ensurePersistence(): Promise<void> {
  if (persistencePromise) return persistencePromise
  persistencePromise = (async () => {
    const existing = readPersistenceFlag()

    if (!existing) {
      try {
        await setPersistence(auth, browserLocalPersistence)
        writePersistenceFlag('local')
      } catch {
        await setPersistence(auth, inMemoryPersistence)
        writePersistenceFlag('memory')
      }
    }
    await waitForAuthInit(auth)
  })()

  return persistencePromise
}

export function firebaseUser(): Promise<User | null> {
  if (!userPromise) {
    userPromise = new Promise((resolve) => {
      userResolver = resolve

      if (auth.currentUser) {
        resolve(auth.currentUser)
        return
      }

      const unsub = onAuthStateChanged(auth, (user) => {
        resolve(user)
        unsub()
      })
    })
  }
  return userPromise
}

export function currentFirebaseUser(): User | null {
  return auth.currentUser
}


export async function getValidIdToken(): Promise<string | null> {
  if (auth.currentUser) {
    try {
      return await auth.currentUser.getIdToken()
    } catch {
      return null
    }
  }

  if (!tokenPromise) {
    tokenPromise = new Promise((resolve) => {
      tokenResolver = resolve
    })
  }

  return tokenPromise
}
if(auth) {
  onIdTokenChanged(auth, async (user) => {
    if (user) {
      const token = await user.getIdToken()
      if (tokenResolver) tokenResolver(token)
      tokenResolver = null
      tokenPromise = Promise.resolve(token)
    } else {
      if (tokenResolver) tokenResolver(null)
      tokenResolver = null
      tokenPromise = Promise.resolve(null)
    }
  })
}

if(auth) {
  onAuthStateChanged(auth, (user) => {
    if (userResolver) userResolver(user)
    userResolver = null
    userPromise = Promise.resolve(user)
  })
}

const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000 // 50 minutes

if(auth) {
  setInterval(async () => {
    const user = auth.currentUser
    if (user) {
      try {
        await user.getIdToken(true)
      } catch (e) {
        console.warn('Failed to refresh Firebase token:', e)
      }
    }
  }, TOKEN_REFRESH_INTERVAL)
}

export const getFirebaseMessaging = (): Messaging => {
  if (!firebaseConfig) return
  try {
    return getMessaging(app)
  } catch (e) {
    console.error('Error: ', e)
  }
}

export const loadToken = async (messaging: Messaging, registration: ServiceWorkerRegistration) => {
  try {
    if (!firebaseConfig) return
    const existingSubscription = await registration.pushManager.getSubscription()
    if (existingSubscription) {
      await existingSubscription.unsubscribe()
    }
    return await getToken(messaging, {
      serviceWorkerRegistration: registration,
      vapidKey: env.FIREBASE_VAPID_KEY,
    })
  } catch (e) {
    console.error('Error: ', e)
    return null
  }
}

export const requestNotificationPermissions = async () => {
  try {
    const permission = await Notification?.requestPermission?.()
    return permission
  } catch (e) {
    console.error('Error Notification Permission Request: ', e)
  }
}

let hasMessagesListener = false
export const handleForegroundMessages = async (messaging: Messaging) => {
  try {
    if (Notification?.permission === 'granted' && !hasMessagesListener) {
      onMessage(messaging, (payload) => {
        if (payload?.data) {
          const not = new Notification(payload.data.title, {
            body: payload.data.body,
            icon: payload.data.icon || BRAND_ICON,
            ...payload,
            tag: `${payload.data.type || payload.data.title}_${payload?.data?.contentId}`,
          })
          not.onclick = () => {
            not.close()
            window.location.href = `${self.location.origin}/inbox`
          }
        }
      })
      hasMessagesListener = true
    }
  } catch (e) {
    console.error(e)
  }
}

export const getFirebaseAnalytics = () => {
  if (!firebaseConfig) return
  try {
    getAnalytics(app)

    // Initialize Performance Monitoring and get a reference to the service
    getPerformance(app)
  } catch (e) {
    console.error('Error: ', e)
  }
}
if (typeof window !== 'undefined') {
  getFirebaseAnalytics()
}

const channel = new BroadcastChannel('firebase-auth-sync')

let currentToken: string | null = null

channel.onmessage = (event) => {
  const data = event.data
  if (data?.type === 'SIGN_OUT') {
    auth.signOut()
  }
  if (data?.type === 'TOKEN_REFRESH' && data.token && auth.currentUser) {
    currentToken = data.token
  }
}

export async function getFirebaseToken(): Promise<string | null> {
  // Check auth.currentUser directly first: after signInWithEmailLink completes,
  // auth.currentUser is set synchronously but the module-level userPromise
  // singleton may still be resolved with the stale null from initial auth-state check.
  const liveUser = auth?.currentUser
  const user = liveUser ?? await firebaseUser()
  console.log('[vb:ml] getFirebaseToken — auth.currentUser:', liveUser ? `uid=${liveUser.uid}` : 'null', 'firebaseUser resolved:', user ? `uid=${user.uid}` : 'null')
  if (!user) return currentToken

  const newToken = await user.getIdToken()
  currentToken = newToken
  return currentToken
}

export function onAuthChange(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback)
}

export const clearFirebaseStorage = async () => {
  localStorage.removeItem('magic_link_email')
  localStorage.removeItem('emailForSignIn')
  localStorage.removeItem(`firebase:authUser:${app.options.apiKey}:[DEFAULT]`)
  localStorage.removeItem(`firebase:redirectEventId:${app.options.apiKey}:[DEFAULT]`)
  indexedDB.deleteDatabase('firebaseLocalStorageDb')
  memoryCache = null
  if (safeLocalStorageAvailable()) {
    localStorage.removeItem(STORAGE_KEY)
  }
}

export async function firebaseLogout(): Promise<void> {
  try {
    await signOut(auth)
  } catch (err) {
    console.error('Firebase signOut error:', err)
  }

  persistencePromise = null
  userPromise = null
  userResolver = null
  tokenPromise = null
  tokenResolver = null

  clearFirebaseStorage()
}

export const appAuth = ensurePersistence

export { app, channel, auth }
